import { useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import type { ApiKeySummary, CatalogModel, CreateApiKeyRequest } from '@nadoutong/contracts';
import { getGatewayBaseUrl, PlatformApiError } from '../api/client.ts';
import { useCatalog } from '../api/useCatalog.ts';
import { useChannelsList } from '../api/useChannels.ts';
import { useCreateApiKey, useKeysList, useRevokeApiKey, useUpdateApiKey } from '../api/useKeys.ts';
import { Modal } from '../components/Modal.tsx';
import { Skeleton } from '../components/Skeleton.tsx';
import { useToast, coerceErrorMessage } from '../components/Toast.tsx';
import { formatTimestamp, formatUsdRate } from '../components/formatters.ts';
import { useClipboard } from '../hooks/useClipboard.tsx';
import { useT } from '../i18n/index.ts';
import { INPUT_CLASS } from '../styles/forms.ts';

function channelCatalogKey(vendor: string, slug: string): string {
  return `${vendor}\x1f${slug}`;
}

function buildCatalogByChannel(
  models: readonly CatalogModel[],
): Map<string, CatalogModel[]> {
  const map = new Map<string, CatalogModel[]>();
  for (const model of models) {
    for (const channel of model.channels) {
      const key = channelCatalogKey(model.vendor, channel.slug);
      const arr = map.get(key) ?? [];
      arr.push(model);
      map.set(key, arr);
    }
  }
  return map;
}

function modelCountLabel(count: number, t: ReturnType<typeof useT>): string {
  return t('keys.models.count', { count: String(count) });
}

function formatModelPriceRange(models: readonly CatalogModel[], multiplier: number): string {
  const prices = models
    .flatMap((model) => [model.input_per_mtok_usd, model.output_per_mtok_usd])
    .filter((value): value is number => typeof value === 'number' && Number.isFinite(value))
    .map((value) => value * multiplier);
  if (prices.length === 0) return '—';
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  if (min === max) return `${formatUsdRate(min)}/MTok`;
  return `${formatUsdRate(min)}-${formatUsdRate(max)}/MTok`;
}

function formatModelCapabilities(models: readonly CatalogModel[]): string {
  const caps = new Set<string>();
  for (const model of models) {
    if (model.supports_streaming) caps.add('stream');
    if (model.supports_tool_use) caps.add('tools');
    if (model.supports_vision) caps.add('vision');
    if (model.supports_prompt_cache) caps.add('cache');
  }
  return caps.size === 0 ? '—' : Array.from(caps).join(' · ');
}

/**
 * API key management surface.
 *
 * Layout:
 *   - Header with "Create key" button.
 *   - Table of existing keys (prefix + name + created + last used +
 *     routing fields + inline rename + delete button). User-facing
 *     history stays simple: revoked rows are treated as deleted and
 *     hidden from the list.
 *   - Modal that surfaces the freshly-created plaintext key. The
 *     plaintext is returned ONCE by platform-api; the modal forces the
 *     user to copy or acknowledge before closing, and shows a warning
 *     that the key cannot be retrieved again.
 *
 * Inline rename uses a small edit-in-place affordance on the table
 * row; revoke fires a confirm modal. Mutation failures surface both
 * inline (keeps caller focus) and via the global toast so operators
 * notice even if the affected row leaves the viewport.
 */

export function Keys(): JSX.Element {
  const t = useT();
  const keysQ = useKeysList();
  const activeKeys = useMemo(
    () => keysQ.data?.keys.filter((key) => key.revokedAt === null) ?? [],
    [keysQ.data],
  );
  const createM = useCreateApiKey();
  const toast = useToast();
  const [createOpen, setCreateOpen] = useState(false);
  const [createdPlaintext, setCreatedPlaintext] = useState<{
    key: string;
    name: string | null;
  } | null>(null);

  return (
    <section data-testid="keys-page" className="flex flex-col gap-8">
      <header className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-col gap-2">
          <h2 className="text-3xl font-semibold tracking-tight text-on-surface">
            {t('keys.title')}
          </h2>
          <p className="text-sm text-outline">{t('keys.subtitle')}</p>
        </div>
        <button
          type="button"
          onClick={() => setCreateOpen(true)}
          data-testid="keys-create-button"
          className="inline-flex items-center justify-center gap-2 rounded-xl bg-primary-container px-5 py-2.5 text-xs font-bold uppercase tracking-[0.08em] text-on-primary transition-all hover:shadow-[0_0_16px_rgba(173,198,255,0.2)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface"
        >
          <span aria-hidden="true" className="material-symbols-outlined text-[18px]">
            add
          </span>
          <span>{t('keys.create')}</span>
        </button>
      </header>

      <div className="flex flex-col gap-3 rounded-xl bg-surface-container-low px-5 py-4 sm:flex-row sm:items-center">
        <div className="min-w-0 flex-1">
          <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline">
            {t('keys.base_url.title')}
          </span>
          <p className="mt-1 text-xs text-outline">{t('keys.base_url.description')}</p>
          <code className="mt-2 block overflow-x-auto font-mono text-sm text-primary">
            {getGatewayBaseUrl()}
          </code>
        </div>
        <button
          type="button"
          onClick={() => {
            void navigator.clipboard.writeText(getGatewayBaseUrl());
            toast.success(t('keys.copied'));
          }}
          className="rounded p-1.5 text-outline hover:text-primary hover:bg-primary/10 transition-colors"
        >
          <span aria-hidden="true" className="material-symbols-outlined text-[16px]">
            content_copy
          </span>
        </button>
      </div>

      <article className="flex flex-col gap-4 rounded-xl bg-surface-container p-6">
        {keysQ.isLoading ? (
          <div className="flex flex-col gap-2" data-testid="keys-loading">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-12" />
            ))}
            <span className="sr-only">{t('keys.loading')}</span>
          </div>
        ) : keysQ.isError ? (
          <p className="text-sm text-error" role="alert" data-testid="keys-error">
            {keysQ.error instanceof PlatformApiError
              ? `${keysQ.error.code}: ${keysQ.error.message}`
              : t('keys.error')}
          </p>
        ) : keysQ.data !== undefined && activeKeys.length === 0 ? (
          <p className="text-sm text-outline" data-testid="keys-empty">
            {t('keys.empty')}
          </p>
        ) : keysQ.data !== undefined ? (
          <KeysTable keys={activeKeys} />
        ) : null}
      </article>

      {createOpen ? (
        <CreateKeyModal
          isPending={createM.isPending}
          onClose={() => setCreateOpen(false)}
          onSubmit={async (name, channelId) => {
            try {
              const body: CreateApiKeyRequest = { channel_id: channelId };
              if (name.length > 0) body.name = name;
              const result = await createM.mutateAsync(body);
              setCreateOpen(false);
              setCreatedPlaintext({ key: result.key, name: result.summary.name });
              return null;
            } catch (err) {
              const message =
                err instanceof PlatformApiError
                  ? `${err.code}: ${err.message}`
                  : coerceErrorMessage(err);
              toast.error(message);
              return message;
            }
          }}
        />
      ) : null}

      {createdPlaintext !== null ? (
        <PlaintextReveal
          plaintext={createdPlaintext.key}
          keyName={createdPlaintext.name}
          onClose={() => setCreatedPlaintext(null)}
        />
      ) : null}
    </section>
  );
}

interface KeysTableProps {
  readonly keys: readonly ApiKeySummary[];
}

function KeysTable({ keys }: KeysTableProps): JSX.Element {
  const t = useT();
  const channelsQ = useChannelsList();
  const catalogQ = useCatalog();
  const channelMap = useMemo(() => {
    const m = new Map<
      string,
      { vendor: string; slug: string; display_name: string; rate_multiplier: number }
    >();
    for (const ch of channelsQ.data ?? []) m.set(ch.id, ch);
    return m;
  }, [channelsQ.data]);
  const catalogByChannel = useMemo(
    () => buildCatalogByChannel(catalogQ.data?.models ?? []),
    [catalogQ.data],
  );
  const updateM = useUpdateApiKey();
  const revokeM = useRevokeApiKey();
  const toast = useToast();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draftName, setDraftName] = useState('');
  const [rowError, setRowError] = useState<{ id: string; message: string } | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<ApiKeySummary | null>(null);

  function startEdit(key: ApiKeySummary): void {
    setEditingId(key.id);
    setDraftName(key.name ?? '');
    setRowError(null);
  }

  async function saveEdit(id: string): Promise<void> {
    setRowError(null);
    const name = draftName.trim();
    if (name.length === 0) {
      setRowError({ id, message: t('keys.row_error.name_required') });
      return;
    }
    try {
      await updateM.mutateAsync({ id, body: { name } });
      setEditingId(null);
      setDraftName('');
    } catch (err) {
      const message =
        err instanceof PlatformApiError
          ? `${err.code}: ${err.message}`
          : coerceErrorMessage(err, t('keys.row_error.network'));
      setRowError({ id, message });
      toast.error(message);
    }
  }

  return (
    <div className="overflow-x-auto" data-testid="keys-table">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="text-left text-[10px] uppercase tracking-[0.1em] text-outline">
            <th className="px-3 py-2 font-semibold">{t('keys.column.prefix')}</th>
            <th className="px-3 py-2 font-semibold">{t('keys.column.name')}</th>
            <th className="px-3 py-2 font-semibold">{t('keys.column.vendor')}</th>
            <th className="px-3 py-2 font-semibold">{t('keys.column.channel')}</th>
            <th className="px-3 py-2 font-semibold">{t('keys.column.rate')}</th>
            <th className="px-3 py-2 font-semibold">{t('keys.column.created')}</th>
            <th className="px-3 py-2 font-semibold">{t('keys.column.last_used')}</th>
            <th className="px-3 py-2 font-semibold text-right">{t('keys.column.actions')}</th>
          </tr>
        </thead>
        <tbody>
          {keys.map((key) => {
            const isEditing = editingId === key.id;
            const channel = key.channelId !== null ? (channelMap.get(key.channelId) ?? null) : null;
            return (
              <tr
                key={key.id}
                data-testid={`keys-row-${key.id}`}
                className="odd:bg-surface-container-low"
              >
                <td className="px-3 py-2">
                  {key.prefix.length > 0 ? (
                    <span className="font-mono text-on-surface-variant">{key.prefix}…</span>
                  ) : (
                    '—'
                  )}
                </td>
                <td className="px-3 py-2 text-on-surface">
                  {isEditing ? (
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={draftName}
                        maxLength={120}
                        onChange={(e) => setDraftName(e.currentTarget.value)}
                        data-testid={`keys-edit-input-${key.id}`}
                        className={INPUT_CLASS}
                      />
                      <button
                        type="button"
                        onClick={() => void saveEdit(key.id)}
                        disabled={updateM.isPending}
                        data-testid={`keys-edit-save-${key.id}`}
                        className="inline-flex items-center justify-center rounded-lg bg-primary-container px-3 py-1 text-[11px] font-bold uppercase tracking-[0.08em] text-on-primary disabled:opacity-40 disabled:pointer-events-none"
                      >
                        {t('keys.save')}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setEditingId(null);
                          setDraftName('');
                          setRowError(null);
                        }}
                        disabled={updateM.isPending}
                        data-testid={`keys-edit-cancel-${key.id}`}
                        className="inline-flex items-center justify-center rounded-lg bg-surface-container-highest px-3 py-1 text-[11px] font-bold uppercase tracking-[0.08em] text-on-surface disabled:opacity-40 disabled:pointer-events-none"
                      >
                        {t('keys.cancel')}
                      </button>
                    </div>
                  ) : (
                    <span data-testid={`keys-name-${key.id}`}>{key.name ?? '—'}</span>
                  )}
                  {rowError !== null && rowError.id === key.id ? (
                    <p
                      className="mt-1 text-[11px] font-medium text-error"
                      role="alert"
                      data-testid={`keys-edit-error-${key.id}`}
                    >
                      {rowError.message}
                    </p>
                  ) : null}
                </td>
                <td
                  className="px-3 py-2 text-on-surface-variant"
                  data-testid={`keys-vendor-${key.id}`}
                >
                  {channel?.vendor ?? '—'}
                </td>
                <td
                  className="px-3 py-2 text-on-surface-variant"
                  data-testid={`keys-channel-${key.id}`}
                >
                  {channel?.display_name ?? '—'}
                </td>
                <td
                  className="px-3 py-2 font-mono text-primary"
                  data-testid={`keys-rate-${key.id}`}
                >
                  {channel !== null ? `×${channel.rate_multiplier.toFixed(2)}` : '—'}
                </td>
                <td className="px-3 py-2 font-mono text-on-surface-variant">
                  {formatTimestamp(key.createdAt)}
                </td>
                <td className="px-3 py-2 font-mono text-on-surface-variant">
                  {key.lastUsedAt !== null ? formatTimestamp(key.lastUsedAt) : t('keys.never_used')}
                </td>
                <td className="px-3 py-2">
                  <div className="flex items-center justify-end gap-2">
                    {channel !== null ? (
                      <span
                        className="rounded bg-surface-container-highest px-2 py-1 text-[10px] font-bold uppercase tracking-[0.08em] text-outline"
                        data-testid={`keys-models-${key.id}`}
                      >
                        {modelCountLabel(
                          catalogByChannel.get(channelCatalogKey(channel.vendor, channel.slug))?.length ?? 0,
                          t,
                        )}
                      </span>
                    ) : null}
                    {isEditing ? null : (
                      <button
                        type="button"
                        onClick={() => startEdit(key)}
                        data-testid={`keys-rename-${key.id}`}
                        className="inline-flex items-center justify-center rounded-lg bg-surface-container-highest px-3 py-1 text-[11px] font-bold uppercase tracking-[0.08em] text-on-surface transition-colors hover:bg-surface-bright disabled:opacity-40 disabled:pointer-events-none"
                      >
                        {t('keys.rename')}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => setRevokeTarget(key)}
                      data-testid={`keys-revoke-${key.id}`}
                      className="inline-flex items-center justify-center rounded-lg bg-error-container px-3 py-1 text-[11px] font-bold uppercase tracking-[0.08em] text-on-error-container transition-all hover:brightness-110 disabled:opacity-40 disabled:pointer-events-none"
                    >
                      {t('keys.revoke')}
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {revokeTarget !== null ? (
        <RevokeKeyModal
          target={revokeTarget}
          isPending={revokeM.isPending}
          onClose={() => setRevokeTarget(null)}
          onConfirm={async () => {
            try {
              await revokeM.mutateAsync(revokeTarget.id);
              setRevokeTarget(null);
              return null;
            } catch (err) {
              const message =
                err instanceof PlatformApiError
                  ? `${err.code}: ${err.message}`
                  : coerceErrorMessage(err);
              toast.error(message);
              return message;
            }
          }}
        />
      ) : null}
    </div>
  );
}

interface CreateKeyModalProps {
  readonly isPending: boolean;
  readonly onClose: () => void;
  readonly onSubmit: (name: string, channelId: string) => Promise<string | null>;
}

function CreateKeyModal({ isPending, onClose, onSubmit }: CreateKeyModalProps): JSX.Element {
  const t = useT();
  const [name, setName] = useState('');
  const [vendorSel, setVendorSel] = useState('');
  const [channelId, setChannelId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const channelsQ = useChannelsList();
  const catalogQ = useCatalog();

  const channels = channelsQ.data ?? [];
  const catalogByChannel = useMemo(
    () => buildCatalogByChannel(catalogQ.data?.models ?? []),
    [catalogQ.data],
  );
  const uniqueVendors = useMemo(
    () => Array.from(new Set(channels.map((c) => c.vendor))).sort(),
    [channels],
  );
  const filteredChannels = useMemo(
    () => channels.filter((c) => c.vendor === vendorSel),
    [channels, vendorSel],
  );
  const selectedChannel = channels.find((c) => c.id === channelId) ?? null;
  const selectedModels =
    selectedChannel === null
      ? []
      : catalogByChannel.get(channelCatalogKey(selectedChannel.vendor, selectedChannel.slug)) ?? [];

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError(null);
    if (channelId.length === 0) {
      setError(t('keys.create_modal.channel_required'));
      return;
    }
    const message = await onSubmit(name.trim(), channelId);
    if (message !== null) {
      setError(message);
    }
  }

  return (
    <Modal
      isOpen
      onClose={() => {
        if (!isPending) onClose();
      }}
      titleId="create-key-modal-title"
      testId="keys-create-modal"
    >
      <form className="flex flex-col gap-6 p-8" onSubmit={handleSubmit} noValidate>
        <h2 id="create-key-modal-title" className="text-base font-semibold text-on-surface">
          {t('keys.create_modal.title')}
        </h2>
        <p className="text-sm text-outline">{t('keys.create_modal.description')}</p>
        <label className="flex flex-col gap-2">
          <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
            {t('keys.create_modal.name_label')}
          </span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.currentTarget.value)}
            disabled={isPending}
            maxLength={120}
            data-testid="keys-create-name"
            placeholder={t('keys.create_modal.name_placeholder')}
            className={INPUT_CLASS}
          />
        </label>
        <label className="flex flex-col gap-2">
          <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
            {t('keys.create_modal.vendor_label')}
          </span>
          <select
            value={vendorSel}
            onChange={(e) => {
              setVendorSel(e.currentTarget.value);
              setChannelId('');
              setError(null);
            }}
            disabled={isPending || channelsQ.isLoading || channelsQ.isError}
            data-testid="keys-create-vendor"
            className={INPUT_CLASS}
            required
          >
            <option value="">{t('keys.create_modal.vendor_placeholder')}</option>
            {uniqueVendors.map((vendor) => (
              <option key={vendor} value={vendor}>
                {vendor}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-2">
          <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
            {t('keys.create_modal.channel_label')}
          </span>
          <select
            value={channelId}
            onChange={(e) => {
              setChannelId(e.currentTarget.value);
              setError(null);
            }}
            disabled={
              isPending ||
              channelsQ.isLoading ||
              channelsQ.isError ||
              vendorSel.length === 0
            }
            data-testid="keys-create-channel"
            className={INPUT_CLASS}
            required
          >
            <option value="">{t('keys.create_modal.channel_placeholder')}</option>
            {filteredChannels.map((ch) => (
              <option
                key={ch.id}
                value={ch.id}
                disabled={catalogQ.isSuccess && !catalogByChannel.has(channelCatalogKey(ch.vendor, ch.slug))}
              >
                {ch.display_name} · ×{ch.rate_multiplier.toFixed(2)}
              </option>
            ))}
          </select>
        </label>
        {selectedChannel !== null ? (
          <ChannelCatalogSummary
            channelName={selectedChannel.display_name}
            rateMultiplier={selectedChannel.rate_multiplier}
            models={selectedModels}
          />
        ) : null}
        {catalogQ.isError ? (
          <p className="text-xs text-outline" role="status">
            {t('keys.create_modal.catalog_error')}
          </p>
        ) : null}
        {channelsQ.isError ? (
          <p className="text-xs font-medium text-error" role="alert">
            {t('keys.create_modal.channels_error')}
          </p>
        ) : !channelsQ.isLoading && channels.length === 0 ? (
          <p className="text-xs font-medium text-error" role="alert">
            {t('keys.create_modal.channels_empty')}
          </p>
        ) : null}
        {error !== null ? (
          <p
            className="text-xs font-medium text-error"
            role="alert"
            data-testid="keys-create-error"
          >
            {error}
          </p>
        ) : null}
        <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={isPending}
            data-testid="keys-create-cancel"
            className="inline-flex items-center justify-center rounded-lg bg-surface-container px-4 py-2 text-xs font-bold uppercase tracking-[0.08em] text-on-surface transition-colors hover:bg-surface-bright disabled:opacity-40 disabled:pointer-events-none"
          >
            {t('keys.create_modal.cancel')}
          </button>
          <button
            type="submit"
            disabled={isPending || channelsQ.isLoading || channelId.length === 0}
            data-testid="keys-create-submit"
            className="inline-flex items-center justify-center rounded-lg bg-primary-container px-4 py-2 text-xs font-bold uppercase tracking-[0.08em] text-on-primary transition-all hover:shadow-[0_0_16px_rgba(173,198,255,0.2)] disabled:opacity-40 disabled:pointer-events-none"
          >
            {isPending ? t('keys.create_modal.submitting') : t('keys.create_modal.submit')}
          </button>
        </div>
      </form>
    </Modal>
  );
}

interface ChannelCatalogSummaryProps {
  readonly channelName: string;
  readonly rateMultiplier: number;
  readonly models: readonly CatalogModel[];
}

function ChannelCatalogSummary({
  channelName,
  rateMultiplier,
  models,
}: ChannelCatalogSummaryProps): JSX.Element {
  const t = useT();
  const preview = models.slice(0, 4);
  return (
    <section
      className="flex flex-col gap-3 rounded-lg bg-surface-container-low px-4 py-3"
      data-testid="keys-create-catalog-summary"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline">
          {channelName}
        </span>
        <span
          className="font-mono text-[11px] text-primary"
          data-testid="keys-create-catalog-price-range"
        >
          {formatModelPriceRange(models, rateMultiplier)}
        </span>
      </div>
      {models.length === 0 ? (
        <p className="text-xs text-error" data-testid="keys-create-catalog-empty">
          {t('keys.create_modal.catalog_empty')}
        </p>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-2 text-xs sm:grid-cols-2">
            <span data-testid="keys-create-catalog-model-count">
              {modelCountLabel(models.length, t)}
            </span>
            <span data-testid="keys-create-catalog-capabilities">
              {formatModelCapabilities(models)}
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            {preview.map((model) => (
              <span
                key={`${model.vendor}/${model.model}`}
                className="rounded bg-surface-container-highest px-2 py-1 font-mono text-[11px] text-on-surface-variant"
              >
                {model.display_name ?? model.model}
              </span>
            ))}
          </div>
        </>
      )}
    </section>
  );
}

interface PlaintextRevealProps {
  readonly plaintext: string;
  readonly keyName: string | null;
  readonly onClose: () => void;
}

function PlaintextReveal({ plaintext, keyName, onClose }: PlaintextRevealProps): JSX.Element {
  const t = useT();
  const { copy, copied, error } = useClipboard();

  return (
    <Modal
      isOpen
      onClose={onClose}
      titleId="plaintext-reveal-title"
      testId="keys-plaintext-modal"
      closeOnBackdrop={false}
    >
      <div className="flex flex-col gap-6 p-8">
        <h2 id="plaintext-reveal-title" className="text-base font-semibold text-on-surface">
          {t('keys.plaintext.title')}
        </h2>
        <p
          className="rounded-lg bg-tertiary-container px-4 py-3 text-sm font-medium text-on-tertiary-container"
          data-testid="keys-plaintext-warning"
          role="alert"
        >
          {t('keys.plaintext.warning')}
        </p>
        <button
          type="button"
          onClick={() => void copy(plaintext)}
          data-testid="keys-plaintext-copy"
          aria-label={t('keys.plaintext.copy_aria_label')}
          className="group flex flex-col items-start gap-2 rounded-lg bg-surface-container-lowest p-4 text-left transition-all hover:bg-surface-container focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          <span className="text-[10px] uppercase tracking-[0.1em] text-outline">
            {keyName !== null && keyName.length > 0
              ? t('keys.plaintext.hint.click_with_name', { name: keyName })
              : t('keys.plaintext.hint.click_to_copy')}
          </span>
          <span
            data-testid="keys-plaintext-value"
            className="w-full break-all font-mono text-lg font-semibold text-primary select-all"
          >
            {plaintext}
          </span>
          <span
            data-testid="keys-plaintext-copy-status"
            className={`text-[10px] uppercase tracking-[0.1em] ${
              copied ? 'text-tertiary' : 'text-outline'
            }`}
          >
            {copied ? t('keys.plaintext.copied') : t('keys.plaintext.copy_anywhere')}
          </span>
        </button>
        {error !== null ? (
          <p
            className="text-[11px] font-medium text-error"
            role="alert"
            data-testid="keys-plaintext-copy-error"
          >
            {error}
          </p>
        ) : null}
        <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            data-testid="keys-plaintext-done"
            className="inline-flex items-center justify-center rounded-lg bg-primary-container px-4 py-2 text-xs font-bold uppercase tracking-[0.08em] text-on-primary transition-all hover:shadow-[0_0_16px_rgba(173,198,255,0.2)]"
          >
            {t('keys.plaintext.done')}
          </button>
        </div>
      </div>
    </Modal>
  );
}

interface RevokeKeyModalProps {
  readonly target: ApiKeySummary;
  readonly isPending: boolean;
  readonly onClose: () => void;
  readonly onConfirm: () => Promise<string | null>;
}

function RevokeKeyModal({
  target,
  isPending,
  onClose,
  onConfirm,
}: RevokeKeyModalProps): JSX.Element {
  const t = useT();
  const [error, setError] = useState<string | null>(null);

  async function handleConfirm(): Promise<void> {
    setError(null);
    const message = await onConfirm();
    if (message !== null) {
      setError(message);
    }
  }

  return (
    <Modal
      isOpen
      onClose={() => {
        if (!isPending) onClose();
      }}
      titleId="revoke-key-modal-title"
      testId="keys-revoke-modal"
    >
      <div className="flex flex-col gap-6 p-8">
        <h2 id="revoke-key-modal-title" className="text-base font-semibold text-on-surface">
          {t('keys.revoke_modal.title')}
        </h2>
        <p className="text-sm text-outline">
          {t('keys.revoke_modal.description.before')}
          <span className="font-mono font-semibold text-on-surface">
            {target.prefix.length > 0 ? `${target.prefix}…` : target.id}
          </span>
          {t('keys.revoke_modal.description.after')}
        </p>
        {error !== null ? (
          <p
            className="text-xs font-medium text-error"
            role="alert"
            data-testid="keys-revoke-error"
          >
            {error}
          </p>
        ) : null}
        <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={isPending}
            data-testid="keys-revoke-cancel"
            className="inline-flex items-center justify-center rounded-lg bg-surface-container px-4 py-2 text-xs font-bold uppercase tracking-[0.08em] text-on-surface transition-colors hover:bg-surface-bright disabled:opacity-40 disabled:pointer-events-none"
          >
            {t('keys.revoke_modal.cancel')}
          </button>
          <button
            type="button"
            onClick={() => void handleConfirm()}
            disabled={isPending}
            data-testid="keys-revoke-confirm"
            className="inline-flex items-center justify-center rounded-lg bg-error-container px-4 py-2 text-xs font-bold uppercase tracking-[0.08em] text-on-error-container transition-all hover:brightness-110 disabled:opacity-40 disabled:pointer-events-none"
          >
            {isPending ? t('keys.revoke_modal.submitting') : t('keys.revoke_modal.submit')}
          </button>
        </div>
      </div>
    </Modal>
  );
}
